import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useGetCurrentUserQuery } from "@/core/api/services"
import { selectUser, setUser } from "@/redux/authSlice"
import { useAppDispatch } from "@/redux/hooks"
import { clerkAuthConfigured, subscribeToClerkSession } from "@/core/auth/clerk"
import { getLegacyAuthToken } from "@/core/auth/legacyToken"

export const useAuth = () => {
  const legacyToken = getLegacyAuthToken()
  const userFromStore = useSelector(selectUser)
  const dispatch = useAppDispatch()
  const isClerkConfigured = clerkAuthConfigured()
  const [clerkState, setClerkState] = useState({
    isLoaded: !isClerkConfigured,
    isSignedIn: false,
  })

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    let active = true

    subscribeToClerkSession((isSignedIn) => {
      if (!active) return
      setClerkState({ isLoaded: true, isSignedIn })
    }).then((cleanup) => {
      if (active) unsubscribe = cleanup
      else cleanup()
    })

    return () => {
      active = false
      unsubscribe?.()
    }
  }, [])

  const hasAuthSession = Boolean(legacyToken || clerkState.isSignedIn)

  const {
    data: currentUser,
    isFetching,
    isError,
  } = useGetCurrentUserQuery(undefined, {
    skip:
      !hasAuthSession ||
      !!userFromStore ||
      (isClerkConfigured && !clerkState.isLoaded),
  })

  useEffect(() => {
    if (currentUser) {
      dispatch(setUser(currentUser))
    } else if (currentUser === null) {
      dispatch(setUser(null))
    }
  }, [currentUser, dispatch])

  return {
    isLoggedIn: !!userFromStore,
    isLoading: isFetching || (isClerkConfigured && !clerkState.isLoaded),
    user: userFromStore,
    isAdmin: userFromStore?.isAdmin,
    token: legacyToken || (clerkState.isSignedIn ? "clerk" : null),
    hasAuthSession,
    isError,
  }
}
